import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { resolveActiveTenantId, useActiveTenantId } from '../lib/tenant';
import type { QualificationPerson, UserProfile } from '../types';
type Page = { tenantId: string; people: QualificationPerson[]; nextCursor: string | null };
const request = (input: object) => httpsCallable<object, Page>(functions, 'listQualificationPeople')(input);
export async function qualificationPersonContext(personId: string): Promise<QualificationPerson> {
  const tenantId = resolveActiveTenantId();
  const result = (await request({ tenantId, personId })).data;
  if (resolveActiveTenantId() !== tenantId || result.tenantId !== tenantId) throw new Error('Tenant changed.');
  return result.people[0];
}
export function useQualificationPeople(profile: UserProfile, assignmentKey: string) {
  const tenantId = useActiveTenantId();
  const identity = `${tenantId}|${profile.id}|${profile.role}|${profile.role === 'admin' ? '' : assignmentKey}`;
  const directory = useRef<{ identity: string; people: QualificationPerson[] } | null>(null);
  const [state, setState] = useState<{ identity: string; data: QualificationPerson[]; loading: boolean; error: Error | null }>({ identity: '', data: [], loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    if (directory.current?.identity !== identity) directory.current = null;
    // Keep the current manager dialog mounted as assignments change; never
    // retain data across a tenant/user/authority change.
    setState(previous => previous.identity === identity
      ? { ...previous, error: null }
      : { identity, data: [], loading: true, error: null });
    void (async () => {
      const people = new Map<string, QualificationPerson>((directory.current?.people ?? []).map(p => [p.personId, p]));
      const seen = new Set<string>();
      let cursor: string | null = null;
      if (!directory.current) do {
        const result: Page = (await request({ tenantId, ...(cursor ? { cursor } : {}) })).data;
        if (cancelled) return;
        if (result.tenantId !== tenantId) throw new Error('Tenant changed.');
        for (const person of result.people) people.set(person.personId, person);
        cursor = result.nextCursor;
        if (cursor && seen.has(cursor)) throw new Error('Invalid Person pagination.');
        if (cursor) seen.add(cursor);
      } while (cursor);
      if (profile.role === 'admin') {
        directory.current = { identity, people: [...people.values()] };
        const roster = (await request({ tenantId, roster: true })).data;
        if (cancelled) return;
        if (roster.tenantId !== tenantId) throw new Error('Tenant changed.');
        for (const person of roster.people) people.set(person.personId, person);
      }
      setState({ identity, data: [...people.values()], loading: false, error: null });
    })().catch(error => { if (!cancelled) setState({ identity, data: [], loading: false, error }); });
    return () => { cancelled = true; };
  }, [identity, tenantId, profile.role, assignmentKey]);
  return state.identity === identity ? state : { data: [], loading: true, error: null };
}
